import moment, { Moment } from "moment";
import {
  CALENDAR_DATE_FORMAT,
  CREW_ROLE_LABELS,
  CREW_TIME_SLOTS,
  JOB_DURATION_DROPDOWN,
  JOB_INACTIVE_STATUSES,
  JOB_RECURRENCE_CUSTOM_MAX_DAYS,
  JOB_RECURRENCE_CUSTOM_MIN_DAYS,
  JOB_TIME_FORMAT,
  JOB_TIME_LABEL_FORMAT,
  JOB_TYPE_FIELDS,
  JOB_TYPE_LABELS,
  JOB_UNKNOWN_TRUCK_LABEL,
  RECURRENCE_DEFAULT_END_MONTHS,
  RECURRENCE_DEFAULT_OCCURRENCES,
  RECURRENCE_WEEK_ORDINALS,
  RECURRENCE_WORKING_WEEKDAYS,
  SCHEDULE_CATEGORY_LABELS,
  TRUCK_STATUS_LABELS,
  TRUCK_UNAVAILABLE_STATUSES,
} from "../constants";
import {
  CrewStatus,
  FieldRequirement,
  JobRecurrence,
  JobType,
  MonthlyRepeatBy,
  RecurrenceEditScope,
  RecurrenceEnd,
  RecurrenceFrequency,
  ScheduleCategory,
} from "../enums";
import {
  AccountBasicInfo,
  CalendarResourceRow,
  CreateJobPayload,
  CrewDay,
  CrewMember,
  DropdownItem,
  Job,
  JobDayLayout,
  JobFormData,
  JobResponse,
  RecurrenceOption,
  RecurrenceRule,
  Truck,
  UpdateJobPayload,
} from "../types";
import { utcToZonedDateTime, zonedDateTimeToUtcIso } from "../utils/dateUtil";

export class JobModel {
  public static isEvent(job: Pick<Job, "type">): boolean {
    return job.type !== JobType.Job;
  }

  public static getCategoryLabel(job: Pick<Job, "type">): string {
    return SCHEDULE_CATEGORY_LABELS[
      JobModel.isEvent(job) ? ScheduleCategory.Event : ScheduleCategory.Job
    ];
  }

  public static getJobTitle(job: Job): string {
    if (!JobModel.isEvent(job)) return job.accountName ?? "";

    if (job.type === JobType.Custom) {
      return job.title ?? JOB_TYPE_LABELS[job.type];
    }

    return job.accountName
      ? `${JOB_TYPE_LABELS[job.type]} · ${job.accountName}`
      : JOB_TYPE_LABELS[job.type];
  }

  public static timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(":").map(Number);

    return hours * 60 + minutes;
  }

  public static minutesToTime(totalMinutes: number): string {
    return `${String(Math.floor(totalMinutes / 60)).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}`;
  }

  public static getTimeLabel(time: string): string {
    const slotLabel = CREW_TIME_SLOTS.find((slot) => slot.value === time)?.key;
    const parsedTime = moment(time, JOB_TIME_FORMAT, true);

    return (
      slotLabel ??
      (parsedTime.isValid() ? parsedTime.format(JOB_TIME_LABEL_FORMAT) : time)
    );
  }

  public static getDurationLabel(minutes: number): string {
    const dropdownLabel = JOB_DURATION_DROPDOWN.find(
      (item) => item.value === minutes,
    )?.key;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (dropdownLabel) return dropdownLabel;
    if (!hours) return `${remainingMinutes} min`;

    return `${hours} hour${hours === 1 ? "" : "s"} ${remainingMinutes} min`;
  }

  public static getTimeRangeLabel(job: Job): string {
    const endMinutes =
      JobModel.timeToMinutes(job.startTime) + job.durationMinutes;
    const endTime = `${String(Math.floor(endMinutes / 60)).padStart(2, "0")}:${String(endMinutes % 60).padStart(2, "0")}`;

    return `${JobModel.getTimeLabel(job.startTime)} – ${JobModel.getTimeLabel(endTime)}`;
  }

  public static isOverlapping(a: Job, b: Job): boolean {
    const startA = JobModel.timeToMinutes(a.startTime);
    const startB = JobModel.timeToMinutes(b.startTime);

    return (
      a.date === b.date &&
      startA < startB + b.durationMinutes &&
      startB < startA + a.durationMinutes
    );
  }

  public static getJobConflicts(
    job: Job,
    jobs: Job[],
    trucks: Truck[],
    crew: CrewMember[],
  ): string[] {
    if (JOB_INACTIVE_STATUSES.includes(job.status)) return [];

    const conflicts: string[] = [];
    const overlappingJobs = jobs.filter(
      (other) =>
        other.id !== job.id &&
        !JOB_INACTIVE_STATUSES.includes(other.status) &&
        JobModel.isOverlapping(job, other),
    );

    if (job.truckId !== null) {
      const truck = trucks.find((item) => item.id === job.truckId);
      const truckCode =
        truck?.truckCode ?? job.truckCode ?? JOB_UNKNOWN_TRUCK_LABEL;

      if (truck && TRUCK_UNAVAILABLE_STATUSES.includes(truck.status)) {
        conflicts.push(`${truckCode} is ${TRUCK_STATUS_LABELS[truck.status]}`);
      }

      overlappingJobs
        .filter((other) => other.truckId === job.truckId)
        .forEach((other) =>
          conflicts.push(
            `${truckCode} is already on ${JobModel.getJobTitle(other)} ${JobModel.getTimeRangeLabel(other)}`,
          ),
        );
    }

    const day = moment(job.date, CALENDAR_DATE_FORMAT);
    const dayKey = day.format("ddd") as CrewDay;
    const jobStart = JobModel.timeToMinutes(job.startTime);
    const jobEnd = jobStart + job.durationMinutes;

    job.crew.forEach((member) => {
      overlappingJobs
        .filter((other) => other.crew.some((item) => item.id === member.id))
        .forEach((other) =>
          conflicts.push(
            `${member.name} is already on ${JobModel.getJobTitle(other)} ${JobModel.getTimeRangeLabel(other)}`,
          ),
        );

      const crewMember = crew.find((item) => item.id === member.id);

      if (!crewMember) return;

      const hours = crewMember.availability[dayKey];

      if (crewMember.status === CrewStatus.OnLeave) {
        conflicts.push(`${member.name} is on leave`);
      } else if (!hours) {
        conflicts.push(
          `${member.name} is unavailable on ${day.format("dddd")}`,
        );
      } else if (
        jobStart < JobModel.timeToMinutes(hours.start) ||
        jobEnd > JobModel.timeToMinutes(hours.end)
      ) {
        conflicts.push(
          `${member.name} is only available ${JobModel.getTimeLabel(hours.start)} – ${JobModel.getTimeLabel(hours.end)} on ${day.format("dddd")}`,
        );
      }
    });

    return conflicts;
  }

  public static buildDayLayout(jobs: Job[]): JobDayLayout[] {
    const sortedJobs = JobModel.sortByStartTime(jobs);
    const layouts: JobDayLayout[] = [];
    let group: JobDayLayout[] = [];
    let groupEnd = 0;

    const closeGroup = () => {
      const laneCount = Math.max(...group.map((item) => item.lane), 0) + 1;

      group.forEach((item) => layouts.push({ ...item, laneCount }));
      group = [];
    };

    sortedJobs.forEach((job) => {
      const start = JobModel.timeToMinutes(job.startTime);

      if (group.length > 0 && start >= groupEnd) closeGroup();

      const usedLanes = group
        .filter(
          (item) =>
            JobModel.timeToMinutes(item.job.startTime) +
              item.job.durationMinutes >
            start,
        )
        .map((item) => item.lane);
      let lane = 0;

      while (usedLanes.includes(lane)) lane += 1;

      group.push({ job, lane, laneCount: 1 });
      groupEnd = Math.max(groupEnd, start + job.durationMinutes);
    });

    if (group.length > 0) closeGroup();

    return layouts;
  }

  public static buildWeekDays(weekStart: Moment): Moment[] {
    return Array.from({ length: 7 }, (_, index) =>
      weekStart.clone().add(index, "days"),
    );
  }

  public static getScheduledHours(jobs: Job[]): number {
    const minutes = jobs
      .filter((job) => !JOB_INACTIVE_STATUSES.includes(job.status))
      .reduce((total, job) => total + job.durationMinutes, 0);

    return Math.round((minutes / 60) * 10) / 10;
  }

  public static buildTruckRows(
    trucks: Truck[],
    jobs: Job[],
    days: Moment[],
  ): CalendarResourceRow[] {
    return trucks.map((truck) => {
      const truckJobs = jobs.filter((job) => job.truckId === truck.id);
      const isUnavailable = TRUCK_UNAVAILABLE_STATUSES.includes(truck.status);

      return {
        id: truck.id,
        title: truck.truckCode,
        subtitle: truck.name,
        scheduledHours: JobModel.getScheduledHours(truckJobs),
        days: Object.fromEntries(
          days.map((day) => {
            const date = day.format(CALENDAR_DATE_FORMAT);

            return [
              date,
              {
                jobs: JobModel.sortByStartTime(
                  truckJobs.filter((job) => job.date === date),
                ),
                hoursLabel: null,
                blockedLabel: isUnavailable
                  ? TRUCK_STATUS_LABELS[truck.status]
                  : null,
              },
            ];
          }),
        ),
      };
    });
  }

  public static buildCrewRows(
    crew: CrewMember[],
    jobs: Job[],
    days: Moment[],
  ): CalendarResourceRow[] {
    return crew.map((member) => {
      const memberJobs = jobs.filter((job) =>
        job.crew.some((item) => item.id === member.id),
      );
      const roleLabel = CREW_ROLE_LABELS[member.roleCode] ?? member.roleName;

      return {
        id: member.id,
        title: `${member.firstName} ${member.lastName}`,
        subtitle: roleLabel,
        scheduledHours: JobModel.getScheduledHours(memberJobs),
        days: Object.fromEntries(
          days.map((day) => {
            const date = day.format(CALENDAR_DATE_FORMAT);
            const hours = member.availability[day.format("ddd") as CrewDay];

            return [
              date,
              {
                jobs: JobModel.sortByStartTime(
                  memberJobs.filter((job) => job.date === date),
                ),
                hoursLabel: hours
                  ? `${JobModel.getTimeLabel(hours.start)} – ${JobModel.getTimeLabel(hours.end)}`
                  : null,
                blockedLabel:
                  member.status === CrewStatus.OnLeave
                    ? "On leave"
                    : hours
                      ? null
                      : "Off",
              },
            ];
          }),
        ),
      };
    });
  }

  public static sortByStartTime(jobs: Job[]): Job[] {
    return [...jobs].sort(
      (a, b) =>
        JobModel.timeToMinutes(a.startTime) -
        JobModel.timeToMinutes(b.startTime),
    );
  }

  public static getRecurrenceLabel(
    recurrence: JobRecurrence,
    date: Moment,
    customDays?: number | null,
  ): string {
    const weekday = date.format("dddd");

    switch (recurrence) {
      case JobRecurrence.Daily:
        return "Daily";
      case JobRecurrence.Weekly:
        return `Weekly on ${weekday}`;
      case JobRecurrence.Monthly:
        return `Monthly on day ${date.date()}`;
      case JobRecurrence.Custom:
        return customDays ? `Every ${customDays} days` : "Custom";
      default:
        return "Does not repeat";
    }
  }

  public static getDefaultRecurrenceRule(date: Moment): RecurrenceRule {
    return {
      frequency: RecurrenceFrequency.Week,
      interval: 1,
      weekDays: [date.day()],
      monthlyRepeatBy: MonthlyRepeatBy.DayOfMonth,
      end: RecurrenceEnd.Never,
      endDate: date.clone().add(RECURRENCE_DEFAULT_END_MONTHS, "months"),
      occurrences: RECURRENCE_DEFAULT_OCCURRENCES,
    };
  }

  public static getWeekOfMonth(date: Moment): number {
    return Math.ceil(date.date() / 7);
  }

  public static getMonthlyRepeatOnLabel(
    repeatBy: MonthlyRepeatBy,
    date: Moment,
  ): string {
    switch (repeatBy) {
      case MonthlyRepeatBy.Weekday:
        return `the ${RECURRENCE_WEEK_ORDINALS[JobModel.getWeekOfMonth(date) - 1]} ${date.format("dddd")}`;
      case MonthlyRepeatBy.LastWeekday:
        return `the last ${date.format("dddd")}`;
      default:
        return `day ${date.date()}`;
    }
  }

  public static buildMonthlyRepeatDropdown(date: Moment): DropdownItem[] {
    const isLastWeekday = date.clone().add(7, "days").month() !== date.month();
    const repeatByOptions = [
      MonthlyRepeatBy.DayOfMonth,
      MonthlyRepeatBy.Weekday,
      ...(isLastWeekday && JobModel.getWeekOfMonth(date) === 4
        ? [MonthlyRepeatBy.LastWeekday]
        : []),
    ];

    return repeatByOptions.map((repeatBy) => ({
      key: `Monthly on ${JobModel.getMonthlyRepeatOnLabel(repeatBy, date)}`,
      value: repeatBy,
    }));
  }

  public static getRecurrenceRuleLabel(
    rule: RecurrenceRule | null,
    date: Moment,
  ): string {
    if (!rule) return "Does not repeat";

    const { frequency, interval } = rule;
    let label: string;

    switch (frequency) {
      case RecurrenceFrequency.Day:
        label = interval === 1 ? "Daily" : `Every ${interval} days`;
        break;
      case RecurrenceFrequency.Week: {
        const weekDays = [...rule.weekDays].sort((a, b) => a - b);
        const isWorkingWeek =
          weekDays.join() === RECURRENCE_WORKING_WEEKDAYS.join();
        const dayNames = weekDays
          .map((day) => moment().day(day).format("dddd"))
          .join(", ");

        if (isWorkingWeek && interval === 1) {
          label = "Every weekday (Monday to Friday)";
        } else {
          label =
            interval === 1
              ? `Weekly on ${dayNames}`
              : `Every ${interval} weeks on ${dayNames}`;
        }
        break;
      }
      case RecurrenceFrequency.Month: {
        const repeatOn = JobModel.getMonthlyRepeatOnLabel(
          rule.monthlyRepeatBy,
          date,
        );
        label =
          interval === 1
            ? `Monthly on ${repeatOn}`
            : `Every ${interval} months on ${repeatOn}`;
        break;
      }
      default: {
        const repeatOn = date.format("MMMM D");
        label =
          interval === 1
            ? `Annually on ${repeatOn}`
            : `Every ${interval} years on ${repeatOn}`;
      }
    }

    if (rule.end === RecurrenceEnd.On) {
      return `${label}, until ${rule.endDate.format("MMM D, YYYY")}`;
    }
    if (rule.end === RecurrenceEnd.After) {
      return `${label}, ${rule.occurrences} times`;
    }

    return label;
  }

  public static buildRecurrenceOptions(date: Moment): RecurrenceOption[] {
    const defaultRule = JobModel.getDefaultRecurrenceRule(date);
    const rules: (RecurrenceRule | null)[] = [
      null,
      { ...defaultRule, frequency: RecurrenceFrequency.Day },
      defaultRule,
      {
        ...defaultRule,
        frequency: RecurrenceFrequency.Month,
        monthlyRepeatBy: MonthlyRepeatBy.Weekday,
      },
      { ...defaultRule, frequency: RecurrenceFrequency.Year },
      { ...defaultRule, weekDays: RECURRENCE_WORKING_WEEKDAYS },
    ];

    return rules.map((rule) => ({
      label: JobModel.getRecurrenceRuleLabel(rule, date),
      rule,
    }));
  }

  public static buildRecurrenceRule(
    recurrence: JobRecurrence,
    recurrenceInterval: number | null,
    date: Moment,
  ): RecurrenceRule | null {
    const defaultRule = JobModel.getDefaultRecurrenceRule(date);

    switch (recurrence) {
      case JobRecurrence.Daily:
        return { ...defaultRule, frequency: RecurrenceFrequency.Day };
      case JobRecurrence.Weekly:
        return defaultRule;
      case JobRecurrence.Monthly:
        return { ...defaultRule, frequency: RecurrenceFrequency.Month };
      case JobRecurrence.Custom:
        return {
          ...defaultRule,
          frequency: RecurrenceFrequency.Day,
          interval: recurrenceInterval ?? 1,
        };
      default:
        return null;
    }
  }

  public static getApiRecurrence(
    rule: RecurrenceRule | null,
    date: Moment,
  ): Pick<JobFormData, "recurrence" | "recurrenceInterval"> | null {
    if (!rule) {
      return { recurrence: JobRecurrence.None, recurrenceInterval: null };
    }
    if (rule.end !== RecurrenceEnd.Never) return null;

    const { frequency, interval } = rule;
    const isJobWeekday =
      rule.weekDays.length === 1 && rule.weekDays[0] === date.day();

    if (interval === 1 && frequency === RecurrenceFrequency.Day) {
      return { recurrence: JobRecurrence.Daily, recurrenceInterval: null };
    }
    if (
      interval === 1 &&
      frequency === RecurrenceFrequency.Week &&
      isJobWeekday
    ) {
      return { recurrence: JobRecurrence.Weekly, recurrenceInterval: null };
    }
    if (
      interval === 1 &&
      frequency === RecurrenceFrequency.Month &&
      rule.monthlyRepeatBy === MonthlyRepeatBy.DayOfMonth
    ) {
      return { recurrence: JobRecurrence.Monthly, recurrenceInterval: null };
    }

    const days =
      frequency === RecurrenceFrequency.Day
        ? interval
        : frequency === RecurrenceFrequency.Week && isJobWeekday
          ? interval * 7
          : NaN;

    if (
      days >= JOB_RECURRENCE_CUSTOM_MIN_DAYS &&
      days <= JOB_RECURRENCE_CUSTOM_MAX_DAYS
    ) {
      return { recurrence: JobRecurrence.Custom, recurrenceInterval: days };
    }

    return null;
  }

  public static buildJobs(jobs: JobResponse[], timeZone: string): Job[] {
    return jobs.map(({ startAt, ...job }) => {
      const { date, time } = utcToZonedDateTime(startAt, timeZone);

      return { ...job, date, startTime: time };
    });
  }

  public static buildJobDetailsPayload(
    job: Job,
    timeZone: string,
  ): Omit<CreateJobPayload, "type" | "recurrence" | "recurrenceInterval"> {
    return {
      title: job.title,
      accountId: job.accountId,
      address: job.address,
      startAt: zonedDateTimeToUtcIso(job.date, job.startTime, timeZone),
      durationMinutes: job.durationMinutes,
      truckId: job.truckId,
      crewIds: job.crew.map((member) => member.id),
      notes: job.notes,
    };
  }

  public static buildCreateJobPayload(
    job: Job,
    timeZone: string,
  ): CreateJobPayload {
    return {
      ...JobModel.buildJobDetailsPayload(job, timeZone),
      type: job.type,
      recurrence: job.recurrence,
      recurrenceInterval: job.recurrenceInterval,
    };
  }

  public static buildUpdateJobPayload(
    job: Job,
    timeZone: string,
    scope: RecurrenceEditScope,
  ): UpdateJobPayload {
    return {
      ...JobModel.buildJobDetailsPayload(job, timeZone),
      status: job.status,
      scope,
    };
  }

  public static buildAccountDropdown(
    accounts: AccountBasicInfo[],
  ): DropdownItem[] {
    return accounts.map((account) => ({
      key: account.accountName,
      value: account.accountId,
    }));
  }

  public static buildTruckDropdown(trucks: Truck[]): DropdownItem[] {
    return trucks.map((truck) => ({
      key: TRUCK_UNAVAILABLE_STATUSES.includes(truck.status)
        ? `${truck.truckCode} · ${truck.name} (${TRUCK_STATUS_LABELS[truck.status]})`
        : `${truck.truckCode} · ${truck.name}`,
      value: String(truck.id),
    }));
  }

  public static buildJob(
    data: JobFormData,
    crewIds: number[],
    references: {
      accounts: AccountBasicInfo[];
      trucks: Truck[];
      crew: CrewMember[];
    },
    job?: Job,
    assignmentRecurrence?: {
      isTruckRecurring: boolean;
      oneTimeCrewIds: number[];
    },
  ): Job {
    const fields = JOB_TYPE_FIELDS[data.type];
    const isShown = (rule: FieldRequirement) =>
      rule !== FieldRequirement.Hidden;
    const truck = isShown(fields.truck)
      ? references.trucks.find((item) => item.id === Number(data.truckId))
      : undefined;
    const accountId =
      isShown(fields.customer) && Number(data.accountId)
        ? Number(data.accountId)
        : null;

    return {
      id: job?.id ?? 0,
      orgId: job?.orgId ?? 0,
      type: data.type,
      title: isShown(fields.title) ? data.title?.trim() || null : null,
      accountId,
      accountName: accountId
        ? (references.accounts.find(
            (account) => account.accountId === accountId,
          )?.accountName ??
          job?.accountName ??
          null)
        : null,
      address: isShown(fields.address) ? data.address?.trim() || null : null,
      date: data.date?.isValid()
        ? data.date.format(CALENDAR_DATE_FORMAT)
        : (job?.date ?? ""),
      startTime: data.startTime,
      durationMinutes: Number(data.durationMinutes),
      truckId: truck?.id ?? null,
      truckCode: truck?.truckCode ?? null,
      isTruckRecurring: assignmentRecurrence?.isTruckRecurring,
      crew: references.crew
        .filter((member) => isShown(fields.crew) && crewIds.includes(member.id))
        .map((member) => ({
          id: member.id,
          name: `${member.firstName} ${member.lastName}`,
          isRecurring: assignmentRecurrence
            ? !assignmentRecurrence.oneTimeCrewIds.includes(member.id)
            : undefined,
        })),
      status: data.status,
      recurrence: isShown(fields.recurrence)
        ? data.recurrence
        : JobRecurrence.None,
      recurrenceInterval:
        isShown(fields.recurrence) && data.recurrence === JobRecurrence.Custom
          ? Number(data.recurrenceInterval)
          : null,
      seriesId: job?.seriesId ?? null,
      notes: data.notes?.trim() || null,
    };
  }
}
